/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardFooter,
  CardBody,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ListGroup,
} from 'reactstrap';
import { withFormik, Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Link, withRouter } from 'react-router-dom';
import { ListGroupItem, Field, FormAlerts, Button, PseudoStates, CardTitle, useFeatures } from '@cordata/buffet';
import { find, chain, compact, isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { all, spread } from 'axios';

import {
  authorizedInstanceNoBase as axiosFull,
  adminInstance as Admin,
  referenceAdminInstance as RefAdmin,
  toastSuccess,
} from '~/utils';
import Dropzone from '~/components/common/Dropzone';
import LocationForm from '~/components/common/location/form/multi';
import useApi from '~/utils/api/hook';

const nameFieldsForDisplayName = ['prefix', 'firstName', 'middleName', 'lastName', 'suffix'];

const serializeAddress = (a, p) => ({
  providerUid: p,
  providerLocationLabel: a.name,
  addr1: a.address1,
  addr2: a.address2,
  city: a.city,
  faxNumber: a.faxNumber && a.faxNumber !== '' ? a.faxNumber.replace(/\D/g, '') : null,
  officeNumber: a.officeNumber && a.officeNumber !== '' ? a.officeNumber.replace(/\D/g, '') : null,
  provinceIso: a.state && a.state.attributes.divisionAbbrevIso,
  postCode: a.postCode,
  activeLocation: true,
  orgUid: localStorage.getItem('org'),
});

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    firstName: Yup.string()
      .max(50)
      .required(),
    lastName: Yup.string()
      .max(50)
      .required(),
    displayName: Yup.string()
      .max(255)
      .required(),
    email: Yup.string().email().nullable(),
    mobileNumber: Yup.string()
      .max(10),
    specialty: Yup.object().shape().nullable()
      .required(),
  }),

  mapPropsToValues: props => ({
    id: props.data && props.data.id,
    npiNumber: props.data && props.data.attributes.npiNumber,
    firstName: props.data ? props.data.relationships.person.attributes.firstName : '',
    lastName: props.data ? props.data.relationships.person.attributes.lastName : '',
    middleName: props.data && props.data.relationships.person.attributes.middleName,
    prefix: props.data && props.data.relationships.person.attributes.prefix,
    suffix: props.data && props.data.relationships.person.attributes.suffix,
    displayName: props.data ? props.data.relationships.person.attributes.displayName : '',
    providerSpecialtyUid: props.data && props.data.attributes.providerSpecialtyUid,
    userUid: props.data && props.data.attributes.userUid,
    signatureLink: props.data &&
      props.data.relationships.providerSignatures[0] &&
      props.data.relationships.providerSignatures[0].attributes.imageLink,
    isInSelections: props.data && props.data.attributes.isInSelections,
    api: props.api,
    existingUser: props.data && props.data.relationships.user,
  }),

  mapPropsToTouched: props => ({
    displayName: props.data && props.data.relationships.person.attributes.displayName !== compact(nameFieldsForDisplayName.map(field => props.data.relationships.person.attributes[field])).join(' '),
  }),

  handleSubmit: (values, { setErrors, setSubmitting, ...bag }) => {
    const { props: { history, api } } = bag;

    const s3UploadPromise = values.file
      ? api
        .post('/s3-upload', {
          ...values.file,
          bucket: process.env.REACT_APP_IMAGES_S3_BUCKET,
          orgUid: localStorage.getItem('org'),
          divisionUid: localStorage.getItem('div'),
        })
      : undefined;

    all([
      s3UploadPromise,
    ])
      .then(spread((s3UploadResult) => {
        const p = {
          isInSelections: values.isInSelections,
          displayName: values.displayName,
          emailAddress: values.emailAddress && values.emailAddress !== '' ? values.emailAddress : undefined,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName,
          npiNumber: values.npiNumber && values.npiNumber.toString(),
          mobileNumber: values.mobileNumber && values.mobileNumber !== '' ? values.mobileNumber.replace(/\D/g, '') : null,
          prefix: values.prefix,
          suffix: values.suffix,
          specialtyUid: (values.specialty || {}).id,
          orgUid: localStorage.getItem('org'),
          divisionUid: localStorage.getItem('div'),
          userUid: (values.user || {}).id,
          signatureLink: s3UploadResult ? s3UploadResult.data.link : values.signatureLink,
        };

        if (!values.id) {
          Admin
            .post('/provider', p)
            .then((r) => {
              const addressPromises = values.addresses ? values.addresses.map(a => (a.id ? Admin.put(`/provider-location/${a.id}`, serializeAddress(a, r.data.data.id)) : Admin.post('/provider-location', serializeAddress(a, r.data.data.id)))) : [];

              return all([
                r,
                ...addressPromises,
              ]);
            })
            .then(spread((r => r)))
            .then(() => toastSuccess())
            .then(() => history.push('/admin/providers'))
            .catch(e => setErrors({ server: e.errors }))
            .then(() => setSubmitting(false));
        } else {
          Admin
            .put(`/provider/${values.id}`, p)
            .then(r => all([
              ...values.addresses.map(a => (a.id ? Admin.put(`/provider-location/${a.id}`, serializeAddress(a, r.data.data.id)) : Admin.post('/provider-location', serializeAddress(a, r.data.data.id)))),
            ]))
            .then(spread((r => r)))
            .then(() => toastSuccess())
            .then(() => history.push('/admin/providers'))
            .catch(e => setErrors({ server: e.errors }))
            .then(() => setSubmitting(false));
        }
      }))
      .catch(console.error)
      .then(() => setSubmitting(false));
  },

  displayName: 'ProvidersGeneralFormik',
  validateOnChange: false,
  validateOnBlur: false,
});

const ProvidersGeneralForm = ({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  validateField,
  setFieldTouched,
  submitCount,
  isSubmitting,
  data,
  status,
  setStatus,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [results, setResults] = useState();
  const { hasAppKickstarter } = useFeatures();
  const [linkVisible, setLinkVisible] = useState(true);
  const { api } = useApi();

  const formControls = {
    values,
    touched,
    errors,
    status,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setStatus,
    submitCount,
  };

  const setFile = file =>
    setFieldValue('file', file);

  useEffect(() => {
    if (values.file) {
      setLinkVisible(false);
      setFieldValue('signatureLink', values.file.base64);
    }
  }, [values.file]);

  const nameFields = nameFieldsForDisplayName.map(field => values[field]);
  const defaultDisplayName = compact(nameFields).join(' ');
  const userTouchedDisplayName = (touched.displayName || false) && defaultDisplayName !== '';

  useEffect(() => {
    validateField('firstName');
  }, [values.firstName]);

  useEffect(() => {
    validateField('specialty');
  }, [values.specialty]);

  useEffect(() => {
    validateField('lastName');
  }, [values.lastName]);

  useEffect(() => {
    validateField('displayName');
  }, [values.displayName]);

  useEffect(() => {
    // auto-set displayName from firstName + lastName unless user manually sets displayName
    if (!userTouchedDisplayName) {
      setFieldValue('displayName', defaultDisplayName);
      setFieldTouched('displayName', false);
    }
  }, nameFields);

  useEffect(() => {
    // if user clears displayName or tabs through displayName after it's been
    // auto-set, allow auto-setting displayName again
    // tabbing through the displayName field sets touched to true, so we need to guard against that
    if (values.displayName === '' || values.displayName === defaultDisplayName) {
      setFieldTouched('displayName', false);
    }
  }, [values.displayName, userTouchedDisplayName]);

  useEffect(() => {
    // if user clears displayName, auto-set displayName after they tab away
    // tabbing away from the field sets touched to true, so need to auto-set displayName after that
    if (userTouchedDisplayName && values.displayName === '') {
      setFieldValue('displayName', defaultDisplayName);
      setFieldTouched('displayName', false);
    }
  }, [userTouchedDisplayName]);

  useEffect(() => {
    if (data) {
      all([
        'personPhones',
        'personEmailAddresses',
      ].map(r => axiosFull.get(data.relationships.person.relationships[r].related)))
        .then(spread((phones, emails) => {
          const email = emails.data.data[0];
          const phoneNumbers = phones.data.data;

          setFieldValue('emailAddress', email && email.attributes.emailAddress);
          setFieldValue('officeNumber', phoneNumbers && find(phoneNumbers, ['attributes.phoneType', 'Work']) && find(phoneNumbers, ['attributes.phoneType', 'Work']).attributes.phoneNumber);
          setFieldValue('mobileNumber', phoneNumbers && find(phoneNumbers, ['attributes.phoneType', 'Mobile']) && find(phoneNumbers, ['attributes.phoneType', 'Mobile']).attributes.phoneNumber);
        }))
        .then(() => setIsLoading(false));

      if (Array.isArray(data.relationships.providerLocations)) {
        setFieldValue('addresses', data.relationships.providerLocations.map(a => ({
          ...a,
          address1: a.attributes.addr1,
          address2: a.attributes.addr2,
          faxNumber: a.attributes.faxNumber,
          officeNumber: a.attributes.officeNumber,
          name: a.attributes.providerLocationLabel,
          city: a.attributes.city,
          state: a.attributes.provinceIso,
          postCode: a.attributes.postCode,
        })));
      }
    } else {
      setIsLoading(false);
    }

    if (values.userUid) {
      Admin
        .get(`/app-user/${values.userUid}`)
        .then(r => r.data.data)
        .then((r) => {
          setFieldValue('user', r);
        });
    }

    Admin
      .get('/app-user-2', {
        params: {
          'all-data': 1,
          sort: 'userName',
          orgUid: localStorage.getItem('org'),
          provider_uid__eq: 'null',
        },
      })
      .then(r => r.data.data)
      .then((r) => {
        if (isEmpty(values.existingUser)) {
          setUsers(r);
        } else {
          setUsers([...r, values.existingUser]);
        }
      });
  }, []);

  useEffect(() => {
    RefAdmin
      .get(`/reference-provider-role/preview?sort=provider-role-label&page=1&page-size=500&$slice_type=organization&$slice_id=${localStorage.getItem('org')}&is-active=true`)
      .then(r => r.data.data)
      .then((r) => {
        setSpecialties(r);
      });
  }, [localStorage.getItem('org')]);

  useEffect(() => {
    if (specialties.length && values.providerSpecialtyUid) {
      RefAdmin
        .get(`/reference-provider-role/${values.providerSpecialtyUid}`)
        .then(r => r.data.data)
        .then((r) => {
          setFieldValue('specialty', r);
        });
    }
  }, [specialties]);

  useEffect(() => {
    if (values.user && values.existingUser?.id !== values.user.id) {
      const u = values.user.attributes;

      setFieldValue('prefix', u.prefix ? u.prefix : '');
      setFieldValue('firstName', u.firstName);
      setFieldValue('middleName', u.middleName ? u.middleName : '');
      setFieldValue('lastName', u.lastName);
      setFieldValue('suffix', u.suffix ? u.suffix : '');
      setFieldValue('displayName', u.displayName);
      setFieldValue('emailAddress', u.emailAddress ? u.emailAddress : '');
      setFieldValue('mobileNumber', u.mobilePhone ? u.mobilePhone : '');
    }
  }, [values.user]);

  const selectResult = (r) => {
    setFieldValue('prefix', chain(r.basic.namePrefix).capitalize().startCase().value());
    setFieldValue('firstName', chain(r.basic.firstName).capitalize().startCase().value());
    setFieldValue('middleName', chain(r.basic.middleName).capitalize().startCase().value());
    setFieldValue('lastName', chain(r.basic.lastName).capitalize().startCase().value());
    setFieldValue('suffix', chain(r.basic.credential).capitalize().startCase().value());
    setFieldValue('displayName', chain(r.basic.name).capitalize().startCase().value());
    setFieldValue('npiNumber', r.number);

    const addresses = r.addresses.map(a => ({
      ...a,
      city: chain(a.city).capitalize().startCase().value(),
      address1: chain(a.address_1).capitalize().startCase().value(),
      address2: chain(a.address_2).capitalize().startCase().value(),
      postCode: a.postal_code,
      name: chain(a.address_purpose).capitalize().startCase().value(),
      officeNumber: a.telephone_number && a.telephone_number.toString().replace(/-/g, ''),
      faxNumber: a.fax_number && a.fax_number.toString().replace(/-/g, ''),
    }));

    setFieldValue('addresses', addresses);

    setIsModalOpen(false);
  };

  return (
    <Card className="rounded-bottom rounded-0 border-top-0">
      {!isLoading ? (
        <Form onSubmit={handleSubmit} noValidate>
          <CardBody>
            { submitCount >= 1 && errors && <FormAlerts errors={errors} /> }

            {!values.id && (
            <CardTitle>User / NPI Lookup</CardTitle>
            )}

            <Row form>
              {!values.id && (
              <Col sm>
                {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                <label>&nbsp;</label>
                <Button
                  type="button"
                  disabled={isSubmitting}
                  block
                  outline
                  color="tertiary"
                  onClick={() => setIsModalOpen(!isModalOpen)}
                >
                  NPI Lookup
                </Button>
              </Col>
              )}

              <Col sm>
                <Field
                  name="user"
                  options={users}
                  type="select"
                  getOptionValue={o => o.id}
                  getOptionLabel={o => (`${o.attributes.lastName}, ${o.attributes.firstName} ( ${o.attributes.userName} )`)}
                />
              </Col>
            </Row>

            {!values.id && (
            <hr />
            )}

            <Row form>
              <Col sm>
                <Field
                  name="npiNumber"
                  label="NPI Number"
                />
              </Col>

              <Col sm>
                <Field
                  name="specialty"
                  options={specialties}
                  type="select"
                  noOptionsMessage={() => (localStorage.getItem('org') ? undefined : 'Please select an organization')}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.providerRoleLabel}
                  required
                />
              </Col>
            </Row>

            <Row form>
              <Col md={1}>
                <Field
                  name="prefix"
                />
              </Col>

              <Col>
                <Field
                  name="firstName"
                  required
                />
              </Col>

              <Col md={2}>
                <Field
                  name="middleName"
                />
              </Col>

              <Col md>
                <Field
                  name="lastName"
                  required
                />
              </Col>

              <Col md={1}>
                <Field
                  name="suffix"
                />
              </Col>
            </Row>

            <Field
              name="displayName"
              required
            />

            <Row form>
              <Col sm>
                <Field
                  name="mobileNumber"
                  type="tel"
                />
              </Col>

              <Col sm>
                <Field
                  name="emailAddress"
                  type="email"
                />
              </Col>
            </Row>

            <Row form>
              <Col sm>
                <Label className="text-primary d-block">
                  Upload file
                </Label>

                <Dropzone
                  setFile={setFile}
                  acceptFileTypes="image/*"
                />
              </Col>

              <Col sm>
                {linkVisible && (
                  <Field
                    name="signatureLink"
                  />
                )}

                <Label className="text-primary d-block">
                  Preview
                </Label>

                <a target="_blank" rel="noreferrer" href={values.signatureLink}>
                  <img className="mw-100" src={values.signatureLink} alt="Invalid link" />
                </a>
              </Col>
            </Row>

            <CardTitle className="mt-5">Locations</CardTitle>

            <FieldArray
              name="addresses"
              render={({ push, remove }) => (
                <>
                  {values.addresses && values.addresses.map((a, i) => (
                    <React.Fragment key={i}>
                      <Button
                        size="sm"
                        outline
                        color="danger"
                        spear
                        type="button"
                        onClick={() => (a.id ? Admin.delete(`/provider-location/${a.id}`).then(() => remove(i)) : remove(i))}
                      >
                        <FontAwesomeIcon icon="minus" className="mr-2" />
                        Remove Address
                      </Button>

                      <LocationForm {...formControls} index={i} includePhones includeName />

                      {i !== values.addresses.length - 1 && <hr />}
                    </React.Fragment>
                  ))}

                  <Button
                    size="sm"
                    outline
                    color="tertiary"
                    spear
                    type="button"
                    onClick={() => push({})}
                  >
                    <FontAwesomeIcon icon="plus" className="mr-2" />
                    Add Address
                  </Button>
                </>
              )}
            />

            {hasAppKickstarter && (
              <Row form>
                <Col sm>
                  <Field
                    name="id"
                    disabled
                  />
                </Col>

                <Col sm>
                  <Field
                    name="personUid"
                    label="Person Uid"
                    placeholder=" "
                    value={data && data.relationships.person && data.relationships.person.id}
                    disabled
                  />
                </Col>

                <Col sm>
                  <Field
                    name="userUid"
                    label="User Uid"
                    placeholder=" "
                    value={data && data.relationships.user && data.relationships.user.id}
                    disabled
                  />
                </Col>
              </Row>
            )}
          </CardBody>

          <CardFooter className="d-flex">
            <div className="ml-auto">
              <Button
                type="cancel"
                disabled={isSubmitting}
                tag={Link}
                to="/admin/providers"
              >
                Cancel
              </Button>

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Working ...' : 'Save'}
              </Button>
            </div>
          </CardFooter>
        </Form>
      ) : (
        <PseudoStates isLoading={isLoading} />
      )}

      <Modal isOpen={isModalOpen} backdrop="static" size="lg" onClosed={() => setResults(undefined)}>
        <ModalHeader>NPI Look-up</ModalHeader>

        {!results ? (
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              postCode: '',
              npiNumber: '',
            }}
            onSubmit={(v, actions) => {
              api
                .get(`/npi-lookup?version=2.1&enumeration_type=NPI-1${v.firstName ? `&first_name=${v.firstName}` : ''}${v.lastName ? `&last_name=${v.lastName}` : ''}${v.npiNumber ? `&number=${v.npiNumber}` : ''}${v.postCode ? `&postal_code=${v.postCode}` : ''}`)
                .then((r) => {
                  setResults(r.data.results);
                })
                .catch(e => actions.setErrors({ server: e }))
                .then(() => actions.setSubmitting(false));
            }}
            render={({
              errors: e,
              handleSubmit: hS,
              isSubmitting: iS,
              submitCount: sC,
            }) => (
              <form onSubmit={hS}>
                <ModalBody>
                  { sC >= 1 && e && <FormAlerts errors={e} /> }

                  <Row form>
                    <Col sm>
                      <Field
                        name="lastName"
                      />
                    </Col>

                    <Col sm>
                      <Field
                        name="firstName"
                      />
                    </Col>
                  </Row>

                  <Row form>
                    <Col sm>
                      <Field
                        name="npiNumber"
                        label="NPI Number"
                      />
                    </Col>

                    <Col sm>
                      <Field
                        name="postCode"
                      />
                    </Col>
                  </Row>
                </ModalBody>

                <ModalFooter>
                  <Button type="cancel" onClick={() => setIsModalOpen(!isModalOpen)}>Cancel</Button>
                  <Button color="primary" icon="search" disabled={iS} onClick={hS}>{iS ? 'Searching ...' : 'Search'}</Button>
                </ModalFooter>
              </form>
            )}
          />
        ) : (
          <>
            <ListGroup>
              {results.length ? results.map(r => (
                <ListGroupItem onClick={() => selectResult(r)} action>
                  {chain(r.basic.lastName).capitalize().startCase().value()}{', '}
                  {chain(r.basic.firstName).capitalize().startCase().value()}{' '}
                  {r.number}
                </ListGroupItem>
              )) : (
                <ListGroupItem>No results found!</ListGroupItem>
              )}
            </ListGroup>

            <ModalFooter>
              <Button type="cancel" onClick={() => setResults(null)}>Back to search</Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </Card>
  );
};

export default withRouter(formEnhancer(ProvidersGeneralForm));
